import React, { useRef, useState, useEffect, useCallback, forwardRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Grid, Environment, useGLTF } from '@react-three/drei';
import { Focus, RefreshCw, Grid3X3, Maximize2, Minimize2, Circle } from 'lucide-react';
import ModelManager from './ModelManager';
import TransformToolbar from './TransformToolbar';

// Background Logo Component - floating like a moon
function BackgroundLogo() {
  const { scene } = useGLTF('/images/3d Logo.gltf');
  const logoRef = useRef();
  const floatOffset = useRef(0);
  const rotationOffset = useRef(0);
  
  useFrame((state, dt) => {
    if (logoRef.current) {
      // Gentle back-and-forth rotation
      rotationOffset.current += dt * 0.3;
      logoRef.current.rotation.y = Math.sin(rotationOffset.current) * 0.3; // Oscillates between -0.3 and 0.3 radians
      
      // Subtle floating motion
      floatOffset.current += dt * 0.5;
      logoRef.current.position.y = 15 + Math.sin(floatOffset.current) * 0.3;
    }
  });
  
  const clonedScene = useMemo(() => {
    if (scene) {
      const cloned = scene.clone();
      
      // Apply chrome appearance matching the home page logo
      cloned.traverse((child) => {
        if (child.isMesh && child.material) {
          // Clone material to avoid affecting other instances
          child.material = child.material.clone();
          
          if (child.material.type === 'MeshStandardMaterial') {
            // Chrome properties matching Logo3D component
            child.material.roughness = 0.05; // Very low for sharp reflections
            child.material.metalness = 1.0; // Full metalness for chrome
            child.material.envMapIntensity = 1.2; // Strong reflections
            child.material.clearcoat = 0.3; // Crisp edge highlights
            child.material.clearcoatRoughness = 0.05; // Sharp clearcoat
            
            // Use lighter charcoal since it's far away
            child.material.color.setHex(0x505050); // Lighter charcoal for visibility
            
            // Slight emissive to help visibility at distance
            child.material.emissive.setHex(0x202020);
            child.material.emissiveIntensity = 0.3;
            
            child.material.needsUpdate = true;
          }
        }
      });
      
      return cloned;
    }
    return null;
  }, [scene]);
  
  if (!clonedScene) return null;
  
  return (
    <group 
      ref={logoRef}
      position={[0, 15, -50]} // Much higher up, above the grid like a moon in the sky
      scale={[1.5, 1.5, 1.5]} // Even smaller size
    >
      <primitive object={clonedScene} />
    </group>
  );
}

function Scene({ 
  spinning, 
  showGrid,
  showRings, 
  groupRef, 
  onReset, 
  loadedModels, 
  onModelLoad, 
  onModelError,
  selectedModelId,
  onModelSelect,
  transformMode,
  onTransformChange,
  isSnappingEnabled,
  constraints,
  onSelectedObjectChange,
  scaleLimits,
  positionLimits
}) {
  const controlsRef = useRef();

  useFrame((_, dt) => {
    if (spinning && groupRef.current) {
      groupRef.current.rotation.y += 0.4 * dt;
    }
  });

  // Expose reset function to parent
  React.useImperativeHandle(onReset, () => ({
    reset: () => {
      if (controlsRef.current) {
        controlsRef.current.target.set(0, 0.9, 0);
        controlsRef.current.update();
      }
    }
  }), []);

  return (
    <>
      <perspectiveCamera
        fov={50}
        position={[2.6, 1.8, 3.2]}
        makeDefault
      />
      
      <OrbitControls
        ref={controlsRef}
        target={[0, 0.9, 0]}
        enableDamping
        dampingFactor={0.08}
        maxPolarAngle={Math.PI * 0.95}
        minDistance={1.2}
        maxDistance={8}
      />

      {/* Enhanced lighting to match upload preview - ensuring consistent appearance */}
      <ambientLight intensity={1.2} />
      <directionalLight 
        position={[10, 10, 5]}
        intensity={1.8} 
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
      />
      <pointLight 
        position={[-10, -10, -5]} 
        intensity={1.0} 
        color="#ffffff"
      />
      {/* Additional fill light for better model illumination */}
      <directionalLight 
        position={[-5, 5, 5]} 
        intensity={1.0} 
        color="#ffffff"
      />
      {/* Top light to brighten the model from above */}
      <directionalLight 
        position={[0, 15, 0]} 
        intensity={1.2} 
        color="#ffffff"
      />

      <group ref={groupRef}>
        {/* Background Logo - floating like a moon - rotates with the stage */}
        {showRings && <BackgroundLogo />}
        
        {/* Intense point lights clustered tightly around the logo - rotates with logo */}
        {showRings && (
          <>
            <pointLight 
              position={[0, 15, -50]} 
              intensity={400.0} 
              color="#ffffff"
              distance={15}
              decay={2}
            />
            <pointLight 
              position={[-5, 15, -50]} 
              intensity={350.0} 
              color="#ffffff"
              distance={12}
              decay={2}
            />
            <pointLight 
              position={[5, 15, -50]} 
              intensity={350.0} 
              color="#ffffff"
              distance={12}
              decay={2}
            />
            <pointLight 
              position={[0, 22, -50]} 
              intensity={400.0} 
              color="#ffffff"
              distance={15}
              decay={2}
            />
            <pointLight 
              position={[0, 8, -50]} 
              intensity={350.0} 
              color="#ffffff"
              distance={12}
              decay={2}
            />
            <pointLight 
              position={[0, 15, -45]} 
              intensity={300.0} 
              color="#ffffff"
              distance={10}
              decay={2}
            />
            <pointLight 
              position={[0, 15, -55]} 
              intensity={300.0} 
              color="#ffffff"
              distance={10}
              decay={2}
            />
            <pointLight 
              position={[-5, 20, -50]} 
              intensity={250.0} 
              color="#ffffff"
              distance={10}
              decay={2}
            />
            <pointLight 
              position={[5, 20, -50]} 
              intensity={250.0} 
              color="#ffffff"
              distance={10}
              decay={2}
            />
          </>
        )}
        
        {showGrid && (
          <Grid
            infiniteGrid
            followCamera
            fadeDistance={100}
            fadeStrength={1}
            cellSize={0.5}
            cellThickness={0.6}
            sectionSize={3}
            sectionThickness={1}
            cellColor="#ffffff"
            sectionColor="#e5e7eb"
            position={[0, -0.0001, 0]}
          />
        )}
        
                 {/* Floor platform boundary outline - modern ring style */}
         {showRings && (
           <>
             {/* Outer ring */}
             <mesh
               rotation={[-Math.PI / 2, 0, 0]}
               position={[0, -0.0005, 0]}
             >
               <ringGeometry args={[4.85, 5, 128]} />
               <meshStandardMaterial 
                 color="#ffffff" 
                 transparent 
                 opacity={0.8}
                 roughness={0.2}
                 metalness={0.6}
                 emissive="#ffffff"
                 emissiveIntensity={0.3}
               />
             </mesh>
             
             {/* Inner ring */}
             <mesh
               rotation={[-Math.PI / 2, 0, 0]}
               position={[0, -0.0004, 0]}
             >
               <ringGeometry args={[0.85, 1.0, 128]} />
               <meshStandardMaterial 
                 color="#ffffff" 
                 transparent 
                 opacity={0.8}
                 roughness={0.2}
                 metalness={0.6}
                 emissive="#ffffff"
                 emissiveIntensity={0.3}
               />
             </mesh>
           </>
         )}
        
        {/* 3D Models */}
        <ModelManager 
          loadedModels={loadedModels}
          onModelLoad={onModelLoad}
          onModelError={onModelError}
          selectedModelId={selectedModelId}
          onModelSelect={onModelSelect}
          transformMode={transformMode}
          onTransformChange={onTransformChange}
          isSnappingEnabled={isSnappingEnabled}
          constraints={constraints}
          onSelectedObjectChange={onSelectedObjectChange}
          scaleLimits={scaleLimits}
          positionLimits={positionLimits}
          orbitControlsRef={controlsRef}
        />
      </group>
    </>
  );
}

const Stage = forwardRef(({ 
  loadedModels = [], 
  onModelLoad, 
  onModelError,
  selectedModelId,
  onModelSelect,
  onTransformChange,
  onClearStage,
  onRemoveModel,
  onSelectedObjectChange
}, ref) => {
  const [spinning, setSpinning] = useState(false);
  const [showGrid, setShowGrid] = useState(true);
  const [showRings, setShowRings] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [transformMode, setTransformMode] = useState('translate');
  const [isSnappingEnabled, setIsSnappingEnabled] = useState(false);
  const [constraints, setConstraints] = useState({ x: true, y: true, z: true });
  const [transformHistory, setTransformHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [selectedObject, setSelectedObject] = useState(null);
  const [selectedModelInitialTransform, setSelectedModelInitialTransform] = useState(null);
  const [currentTransformValues, setCurrentTransformValues] = useState({});
  const [scaleLimits, setScaleLimits] = useState({ min: 0.1, max: 3.0 });
  const [positionLimits, setPositionLimits] = useState({ 
    x: { min: -5, max: 5 }, 
    y: { min: 0, max: 5 }, 
    z: { min: -5, max: 5 } 
  });

  const groupRef = useRef();
  const resetRef = useRef();
  const stageRef = useRef();

  // Forward the ref to the parent component
  React.useImperativeHandle(ref, () => ({
    getCanvas: () => stageRef.current?.querySelector('canvas'),
    captureScreenshot: () => {
      const canvas = stageRef.current?.querySelector('canvas');
      if (!canvas) {
        console.log('No canvas found for screenshot');
        return null;
      }
      
      try {
        // Ensure the canvas is ready and has content
        const context = canvas.getContext('2d');
        if (!context) {
          console.log('No 2D context available');
          return null;
        }
        
        // Check if canvas has any content (not just black)
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        let hasContent = false;
        
        // Check if there are any non-black pixels
        for (let i = 0; i < data.length; i += 4) {
          if (data[i] > 10 || data[i + 1] > 10 || data[i + 2] > 10) {
            hasContent = true;
            break;
          }
        }
        
        if (!hasContent) {
          console.log('Canvas appears to be empty/black, waiting for render...');
          // Wait a bit for the scene to render and try multiple times
          return new Promise((resolve) => {
            let attempts = 0;
            const maxAttempts = 3;
            
            const tryCapture = () => {
              attempts++;
              setTimeout(() => {
                const screenshot = canvas.toDataURL('image/png', 0.9);
                console.log(`Delayed screenshot attempt ${attempts}, length:`, screenshot.length);
                
                // Check if we got a valid screenshot (not just black)
                if (screenshot.length > 1000 || attempts >= maxAttempts) {
                  resolve(screenshot);
                } else {
                  tryCapture();
                }
              }, 300);
            };
            
            tryCapture();
          });
        }
        
        const screenshot = canvas.toDataURL('image/png', 0.9);
        console.log('Screenshot captured successfully, length:', screenshot.length);
        return screenshot;
      } catch (error) {
        console.error('Error capturing screenshot:', error);
        return null;
      }
    }
  }), []);

  // Reset transform history when stage is cleared
  useEffect(() => {
    if (loadedModels.length === 0) {
      console.log(`[Stage] Clearing transform history - stage cleared`);
      setTransformHistory([]);
      setHistoryIndex(-1);
      // Reset transform-related state to defaults
      setTransformMode('translate');
      setIsSnappingEnabled(false);
      setConstraints({ x: true, y: true, z: true });
      
      // CRITICAL: Clear selection when stage is cleared to remove transform controls
      if (onModelSelect) {
        console.log(`[Stage] Clearing model selection - stage cleared`);
        onModelSelect(null);
      }
    }
  }, [loadedModels, onModelSelect]);

  // Handle transform mode change
  const handleTransformModeChange = useCallback((mode) => {
    setTransformMode(mode);
  }, []);

  // Handle snapping toggle
  const handleSnappingToggle = useCallback(() => {
    setIsSnappingEnabled(prev => !prev);
  }, []);

  // Handle axis constraints toggle
  const handleConstraintsToggle = useCallback((axis) => {
    setConstraints(prev => ({
      ...prev,
      [axis]: !prev[axis]
    }));
  }, []);

  // Handle undo
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      const previousTransform = transformHistory[newIndex];
      if (previousTransform && onTransformChange) {
        onTransformChange(previousTransform);
      }
    }
  }, [historyIndex, transformHistory, onTransformChange]);

  // Handle redo
  const handleRedo = useCallback(() => {
    if (historyIndex < transformHistory.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      const nextTransform = transformHistory[newIndex];
      if (nextTransform && onTransformChange) {
        onTransformChange(nextTransform);
      }
    }
  }, [historyIndex, transformHistory, onTransformChange]);

  // Add transform to history
  const addToHistory = useCallback((transformData) => {
    const newHistory = transformHistory.slice(0, historyIndex + 1);
    newHistory.push(transformData);
    setTransformHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [transformHistory, historyIndex]);

  // Handle transform changes from ModelManager
  const handleTransformChange = useCallback((transformData) => {
    if (onTransformChange) {
      onTransformChange(transformData);
    }
    
    // Store current transform values for the numeric panel
    if (transformData.currentValues) {
      setCurrentTransformValues(transformData.currentValues);
    }
    
    // Add to history if it's a real transform update
    if (transformData.transformUpdated) {
      addToHistory(transformData);
    }
  }, [onTransformChange, addToHistory]);

  // Handle selected object changes from ModelManager
  const handleSelectedObjectChange = useCallback((object) => {
    setSelectedObject(object);
    
    // Don't clear initial transform immediately - let the useEffect handle it
    // setSelectedModelInitialTransform(null);
  }, []);

  // Update initial transform when selected model changes
  useEffect(() => {
    if (selectedModelId && window.modelInitialTransforms) {
      const initialTransform = window.modelInitialTransforms.get(selectedModelId);
      if (initialTransform) {
        setSelectedModelInitialTransform(initialTransform);
        console.log(`[Stage] Retrieved initial transform for ${selectedModelId}:`, initialTransform);
      } else {
        console.warn(`[Stage] No initial transform found for ${selectedModelId}`);
        setSelectedModelInitialTransform(null);
      }
    } else {
      setSelectedModelInitialTransform(null);
    }
  }, [selectedModelId]);

  // Handle model load to store initial transforms
  const handleModelLoad = useCallback((modelId, scene, metadata) => {
    console.log(`[Stage] Model loaded: ${modelId}`, metadata);
    
    // Store initial transform data for this model
    if (metadata && metadata.initialTransform) {
      console.log(`[Stage] Initial transform data received:`, metadata.initialTransform);
      
      // Convert array format to object format for easier use
      const initialTransform = {
        position: {
          x: metadata.initialTransform.position[0] || 0,
          y: metadata.initialTransform.position[1] || 0,
          z: metadata.initialTransform.position[2] || 0
        },
        rotation: {
          x: metadata.initialTransform.rotation[0] || 0,
          y: metadata.initialTransform.rotation[1] || 0,
          z: metadata.initialTransform.rotation[2] || 0
        },
        scale: {
          x: metadata.initialTransform.scale[0] || 1,
          y: metadata.initialTransform.scale[1] || 1,
          z: metadata.initialTransform.scale[2] || 1
        }
      };
      
      // Store in a way that we can retrieve by modelId
      if (!window.modelInitialTransforms) {
        window.modelInitialTransforms = new Map();
      }
      window.modelInitialTransforms.set(modelId, initialTransform);
      
      console.log(`[Stage] Stored initial transform for ${modelId}:`, initialTransform);
      console.log(`[Stage] All stored transforms:`, Array.from(window.modelInitialTransforms.entries()));
      
      // If this is the currently selected model, update the state
      if (selectedModelId === modelId) {
        setSelectedModelInitialTransform(initialTransform);
        console.log(`[Stage] Updated selected model initial transform:`, initialTransform);
      }
    } else {
      console.warn(`[Stage] No initial transform data received for ${modelId}`);
    }
    
    if (onModelLoad) {
      onModelLoad(modelId, scene, metadata);
    }
  }, [onModelLoad, selectedModelId]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Don't handle shortcuts if user is typing in an input field
      const target = event.target;
      const isInputField = target.tagName === 'INPUT' || 
                          target.tagName === 'TEXTAREA' || 
                          target.contentEditable === 'true' ||
                          target.closest('[contenteditable="true"]');
      
      if (isInputField) {
        return;
      }

      switch (event.key.toLowerCase()) {
        case 'g':
          event.preventDefault();
          setTransformMode('translate');
          break;
        case 'r':
          event.preventDefault();
          setTransformMode('rotate');
          break;
        case 's':
          event.preventDefault();
          setTransformMode('scale');
          break;
        case 'escape':
          event.preventDefault();
          if (onModelSelect) {
            onModelSelect(null);
          }
          break;
        case 'backspace':
        case 'delete':
          // Remove selected model when Backspace or Delete is pressed
          if (selectedModelId && onRemoveModel) {
            event.preventDefault();
            onRemoveModel(selectedModelId);
          }
          break;
        case 'z':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            if (event.shiftKey) {
              handleRedo();
            } else {
              handleUndo();
            }
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onModelSelect, handleUndo, handleRedo, selectedModelId, onRemoveModel]);

  const handleReset = () => {
    if (groupRef.current) {
      groupRef.current.rotation.set(0, 0, 0);
    }
    if (resetRef.current) {
      resetRef.current.reset();
    }
  };

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await stageRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (err) {
      console.log('Fullscreen error:', err);
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toolbarStyles = {
    button: 'w-9 h-9 rounded-lg bg-[#1a1a1a] hover:bg-[#2a2a2a] border border-[#2a2a2a] hover:border-[#3a3a3a] transition-all duration-200 flex items-center justify-center text-white',
    active: 'bg-[#3a3a3a] border-[#4a4a4a] text-white'
  };

  return (
    <div 
      ref={stageRef}
      className="relative isolate rounded-2xl bg-black ring-1 ring-white/30 overflow-hidden transition-all duration-300 h-[72vh] min-h-[560px]"
      onDoubleClick={(event) => {
        // Clear selection when double-clicking on the stage background
        if (event.target === event.currentTarget) {
          if (onModelSelect) {
            onModelSelect(null);
          }
        }
      }}
    >
      <Canvas 
        className="absolute inset-0" 
        dpr={[1, 2]}
        gl={{
          antialias: true,
          alpha: true,
          shadowMap: {
            enabled: true,
            type: 2 // THREE.PCFSoftShadowMap
          }
        }}
        onDoubleClick={(event) => {
          // Clear selection when double-clicking on empty space in the canvas
          if (event.target === event.currentTarget) {
            if (onModelSelect) {
              onModelSelect(null);
            }
          }
        }}
      >
        <Scene 
          spinning={spinning} 
          showGrid={showGrid}
          showRings={showRings} 
          groupRef={groupRef}
          onReset={resetRef}
          loadedModels={loadedModels}
          onModelLoad={handleModelLoad}
          onModelError={onModelError}
          selectedModelId={selectedModelId}
          onModelSelect={onModelSelect}
          transformMode={transformMode}
          onTransformChange={handleTransformChange}
          isSnappingEnabled={isSnappingEnabled}
          constraints={constraints}
          onSelectedObjectChange={handleSelectedObjectChange}
          scaleLimits={scaleLimits}
          positionLimits={positionLimits}
        />
      </Canvas>
      
      {/* Transform Toolbar */}
      <TransformToolbar
        transformMode={transformMode}
        onModeChange={handleTransformModeChange}
        onSnappingToggle={handleSnappingToggle}
        onConstraintsToggle={handleConstraintsToggle}
        onUndo={handleUndo}
        onRedo={handleRedo}
        canUndo={historyIndex > 0}
        canRedo={historyIndex < transformHistory.length - 1}
        isSnappingEnabled={isSnappingEnabled}
        constraints={constraints}
        selectedObject={selectedObject}
        selectedModelId={selectedModelId}
        onTransformChange={handleTransformChange}
        currentTransformValues={currentTransformValues}
        initialTransform={selectedModelInitialTransform}
        scaleLimits={scaleLimits}
        positionLimits={positionLimits}
      />
      
      {/* Floating Toolbar */}
      <div className="absolute right-3 top-3 z-40 flex gap-2">
        <button
          onClick={handleReset}
          className={toolbarStyles.button}
          title="Reset View"
        >
          <Focus size={18} />
        </button>
        
                 <button
           onClick={() => setSpinning(!spinning)}
           className={`w-9 h-9 rounded-lg transition-all duration-200 flex items-center justify-center ${
             spinning 
               ? `${toolbarStyles.button} ${toolbarStyles.active}`
               : toolbarStyles.button
           }`}
           title="Auto Rotate"
         >
           <RefreshCw size={18} className={spinning ? 'animate-spin' : ''} />
         </button>
        
        <button
          onClick={() => setShowGrid(!showGrid)}
          className={`w-9 h-9 rounded-lg transition-all duration-200 flex items-center justify-center ${
            showGrid 
              ? `${toolbarStyles.button} ${toolbarStyles.active}`
              : toolbarStyles.button
          }`}
          title="Grid"
        >
          <Grid3X3 size={18} />
        </button>
        
        <button
          onClick={() => setShowRings(!showRings)}
          className={`w-9 h-9 rounded-lg transition-all duration-200 flex items-center justify-center ${
            showRings 
              ? `${toolbarStyles.button} ${toolbarStyles.active}`
              : toolbarStyles.button
          }`}
          title="Rings"
        >
          <Circle size={18} />
        </button>

                 <button
           onClick={toggleFullscreen}
           className={`w-9 h-9 rounded-lg transition-all duration-200 flex items-center justify-center ${
             isFullscreen 
               ? `${toolbarStyles.button} ${toolbarStyles.active}`
               : toolbarStyles.button
           }`}
           title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
         >
           {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
         </button>
      </div>

      {/* Clear Stage Button - Bottom Right */}
      {loadedModels.length > 0 && onClearStage && (
        <div className="absolute bottom-3 right-3 z-40">
          <button
            onClick={onClearStage}
            className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-medium border border-red-400/50 rounded-xl transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-red-500/25"
          >
            Clear Stage ({loadedModels.length})
          </button>
        </div>
      )}
    </div>
  );
});

// Preload the 3D logo model for better performance
if (typeof window !== 'undefined') {
  useGLTF.preload('/images/3d Logo.gltf');
}

export default Stage;
